import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import {
  faSearch,
  faPlus,
  faSeedling,
  faPen,
  faChevronDown,
  faChevronRight,
  faExclamationCircle,
  faSyncAlt,
  faLeaf,
  faListUl,
  faBug,
  faLayerGroup,
  faFilter,
  faTimes,
  faChevronUp,
  faBookOpen,
  faCheckCircle,
  faFlask,
  faTint,
  faThLarge,
  faImage,
  faSave,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import CropFormDrawer from "./CropFormDrawer";
import AddIssueDrawer from "./AddIssueDrawer";
import getSearchIssues from "../IssueHomePage/getSearchIssues";
import getAllUsers from "../IssueDetails/getAllUsers";
import getAllCrops from "../IssueDetails/getAllCrops";
import getProducts from "../IssueDetails/getProducts";
import getProblemDetail from "./getProblemDetail";
import saveProblemDetail from "./saveProblemDetail";
import uploadProblemImage from "./uploadProblemImage";
import ImageCropModal from "../reusable/ImageCropModal";
import { ProductDetailDrawer } from "../ProductsandLabels/ProductsandLabels";
import moment from "moment";
import { toBePartiallyChecked } from "@testing-library/jest-dom/dist/matchers";

/* ─── Palette ──────────────────────────────────────────────────────────────── */
const C = {
  dark: "#1a3328",
  mid: "#2d5a3d",
  bright: "#3d8c5e",
  light: "#e8f5ee",
  page: "#f4f6f5",
  border: "#d8e8dc",
  white: "#ffffff",
  textDk: "#1a2e22",
  textGr: "#6c757d",
  red: "#c62828",
  redBg: "#fdecea",
  blue: "#1565c0",
  blueBg: "#e3f2fd",
  orange: "#e65100",
  orangeBg: "#fff3e0",
};

/* ─── react-select styles (searchable product dropdown) ──────────────────────── */
const productSelectSt = {
  control: (b, s) => ({
    ...b, minHeight: 34, borderRadius: 7, fontSize: "0.82rem",
    borderColor: s.isFocused ? C.bright : C.border, boxShadow: "none",
    "&:hover": { borderColor: C.bright },
  }),
  option: (b, s) => ({
    ...b, fontSize: "0.82rem",
    backgroundColor: s.isSelected ? C.mid : s.isFocused ? C.light : "#fff",
    color: s.isSelected ? "#fff" : C.textDk,
  }),
  placeholder: (b) => ({ ...b, fontSize: "0.82rem", color: "#aab8b0" }),
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  valueContainer: (b) => ({ ...b, padding: "0 8px" }),
};

/* ─── Status badge ──────────────────────────────────────────────────────────── */
const STATUS_MAP = {
  New: { bg: "#e3f2fd", color: "#1565c0" },
  "In Progress": { bg: "#fff3e0", color: "#e65100" },
  SolutionProvided: { bg: "#e8f5e9", color: "#2e7d32" },
  Done: { bg: C.light, color: C.mid },
  "On Hold": { bg: "#eceff1", color: "#607d8b" },
  "In Follow": { bg: "#f3e5f5", color: "#7b1fa2" },
  Rejected: { bg: C.redBg, color: C.red },
};
const ss = (s) => STATUS_MAP[s] || { bg: "#f5f5f5", color: "#555" };

/* ─── Small reusable button ─────────────────────────────────────────────────── */
const Btn = ({ icon, label, onClick, style, small, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      height: small ? 30 : 36,
      padding: small ? "0 10px" : "0 14px",
      borderRadius: 8,
      border: "none",
      cursor: disabled ? "not-allowed" : "pointer",
      fontSize: small ? "0.75rem" : "0.82rem",
      fontWeight: 600,
      opacity: disabled ? 0.6 : 1,
      ...style,
    }}
  >
    {icon && (
      <FontAwesomeIcon
        icon={icon}
        style={{ fontSize: small ? "0.65rem" : "0.72rem" }}
      />
    )}
    {label}
  </button>
);

/* ─── Category accordion row ─────────────────────────────────────────────────── */
const CategoryRow = ({ category }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 6 }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0.55rem 0.85rem",
          borderRadius: 9,
          cursor: "pointer",
          backgroundColor: open ? C.light : "#f8faf9",
          border: `1px solid ${open ? C.border : "#eef2ef"}`,
          transition: "all 0.15s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FontAwesomeIcon
            icon={faLayerGroup}
            style={{ color: C.mid, fontSize: "0.72rem" }}
          />
          <span
            style={{ fontSize: "0.87rem", fontWeight: 600, color: C.textDk }}
          >
            {category.category}
          </span>
          <span
            style={{
              fontSize: "0.68rem",
              fontWeight: 700,
              padding: "1px 7px",
              borderRadius: 8,
              backgroundColor: C.light,
              color: C.mid,
            }}
          >
            {category.problems?.length || 0} problems
          </span>
        </div>
        <FontAwesomeIcon
          icon={open ? faChevronDown : faChevronRight}
          style={{ color: C.textGr, fontSize: "0.65rem" }}
        />
      </div>
      {open && (
        <div
          style={{
            marginTop: 4,
            marginLeft: 12,
            borderLeft: `2px solid ${C.border}`,
            paddingLeft: 12,
          }}
        >
          {!category.problems || category.problems.length === 0 ? (
            <div
              style={{
                padding: "0.5rem 0",
                fontSize: "0.8rem",
                color: C.textGr,
              }}
            >
              No problems listed
            </div>
          ) : (
            category.problems.map((p, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  padding: "0.4rem 0.6rem",
                  borderRadius: 7,
                  marginBottom: 3,
                  backgroundColor: "#fafcfb",
                }}
              >
                <FontAwesomeIcon
                  icon={faBug}
                  style={{
                    color: C.textGr,
                    fontSize: "0.68rem",
                    marginTop: 3,
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: "0.83rem", color: C.textDk }}>
                  {p.problemText}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Issue row ─────────────────────────────────────────────────────────────── */
const IssueRow = ({ issue }) => {
  const st = ss(issue.status);
  return (
    <Link
      to={{
        pathname: `/viewIssue/${issue.issueCode}`,
        state: { from: "/crop", tab: "issues" },
      }}
      style={{ textDecoration: "none" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          padding: "0.6rem 0.85rem",
          borderRadius: 9,
          marginBottom: 5,
          backgroundColor: C.white,
          border: `1px solid ${C.border}`,
          transition: "box-shadow 0.15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 2,
            }}
          >
            <span
              style={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: C.mid,
                fontFamily: "monospace",
              }}
            >
              {issue.issueCode}
            </span>
            {issue.cropID?.crop && (
              <span style={{ fontSize: "0.68rem", color: C.textGr }}>
                · {issue.cropID.crop}
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: C.textDk,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {issue.title || issue.description || "—"}
          </div>
          <div style={{ fontSize: "0.7rem", color: C.textGr, marginTop: 2 }}>
            {issue.farmerID?.fullName || ""}
            {issue.farmerID?.fullName && issue.updatedAt ? " · " : ""}
            {issue.updatedAt ? moment(issue.updatedAt).fromNow() : ""}
          </div>
        </div>
        <span
          style={{
            fontSize: "0.68rem",
            fontWeight: 700,
            padding: "3px 9px",
            borderRadius: 9,
            backgroundColor: st.bg,
            color: st.color,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {issue.status}
        </span>
      </div>
    </Link>
  );
};

/* ─── Inline search form for Issues tab ─────────────────────────────────────── */
const STATUS_OPTIONS = [
  "New",
  "In Progress",
  "SolutionProvided",
  "Done",
  "On Hold",
  "In Follow",
  "Rejected",
];

const IssueSearchForm = ({ allUsers, allCrops, onSearch, onReset }) => {
  const [issueCode, setIssueCode] = useState("");
  const [reporter, setReporter] = useState("");
  const [assignee, setAssignee] = useState("");
  const [cropID, setCropID] = useState("");
  const [categoryID, setCategoryID] = useState("");
  const [problemID, setProblemID] = useState("");
  const [status, setStatus] = useState("");

  const sel = allCrops.find((c) => c._id === cropID);
  const categories = sel?.categories || [];
  const cat = categories.find((c) => c._id === categoryID);
  const problems = cat?.problems || [];

  const handleSearch = () => {
    const vals = {
      issueCode: issueCode || null,
      reporter: reporter || null,
      assignee: assignee || null,
    };
    if (cropID) vals.cropID = cropID;
    if (categoryID) vals.categoryID = categoryID;
    if (problemID) vals.problemID = problemID;
    if (status) vals.status = status;
    onSearch(vals);
  };

  const handleReset = () => {
    setIssueCode("");
    setReporter("");
    setAssignee("");
    setCropID("");
    setCategoryID("");
    setProblemID("");
    setStatus("");
    onReset();
  };

  const sf = {
    height: 30,
    borderRadius: 7,
    border: `1.5px solid ${C.border}`,
    padding: "0 8px",
    fontSize: "0.78rem",
    outline: "none",
    color: C.textDk,
    backgroundColor: C.white,
    boxSizing: "border-box",
  };

  return (
    <div
      style={{
        backgroundColor: C.white,
        borderRadius: 10,
        border: `1px solid ${C.border}`,
        padding: "0.55rem 0.85rem",
        marginBottom: "0.85rem",
        boxShadow: "0 1px 4px rgba(20,50,30,0.04)",
      }}
    >
      {/* Row 1: main filters */}
      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <FontAwesomeIcon
          icon={faFilter}
          style={{ color: C.mid, fontSize: "0.68rem", flexShrink: 0 }}
        />

        <input
          value={issueCode}
          onChange={(e) => setIssueCode(e.target.value)}
          placeholder="Query Code"
          style={{ ...sf, width: 108 }}
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{ ...sf, width: 128 }}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
          style={{ ...sf, width: 130 }}
        >
          <option value="">All Assignees</option>
          {allUsers.map((u) => (
            <option key={u._id} value={u._id}>
              {u.fullName}
            </option>
          ))}
        </select>
        <select
          value={reporter}
          onChange={(e) => setReporter(e.target.value)}
          style={{ ...sf, width: 130 }}
        >
          <option value="">All Reporters</option>
          {allUsers.map((u) => (
            <option key={u._id} value={u._id}>
              {u.fullName}
            </option>
          ))}
        </select>
        <select
          value={cropID}
          onChange={(e) => {
            setCropID(e.target.value);
            setCategoryID("");
            setProblemID("");
          }}
          style={{ ...sf, width: 118 }}
        >
          <option value="">All Crops</option>
          {allCrops.map((c) => (
            <option key={c._id} value={c._id}>
              {c.crop}
            </option>
          ))}
        </select>

        {/* cascade: only when crop is selected */}
        {cropID && (
          <select
            value={categoryID}
            onChange={(e) => {
              setCategoryID(e.target.value);
              setProblemID("");
            }}
            style={{ ...sf, width: 130 }}
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat._id} value={cat._id}>
                {cat.category}
              </option>
            ))}
          </select>
        )}
        {cropID && categoryID && (
          <select
            value={problemID}
            onChange={(e) => setProblemID(e.target.value)}
            style={{ ...sf, width: 140 }}
          >
            <option value="">All Problems</option>
            {problems.map((p) => (
              <option key={p._id} value={p._id}>
                {p.problemText}
              </option>
            ))}
          </select>
        )}

        <div
          style={{ marginLeft: "auto", display: "flex", gap: 5, flexShrink: 0 }}
        >
          <button
            onClick={handleReset}
            style={{
              height: 30,
              padding: "0 12px",
              borderRadius: 7,
              border: `1.5px solid ${C.border}`,
              cursor: "pointer",
              fontSize: "0.75rem",
              fontWeight: 600,
              color: C.textGr,
              backgroundColor: C.white,
            }}
          >
            Reset
          </button>
          <button
            onClick={handleSearch}
            style={{
              height: 30,
              padding: "0 14px",
              borderRadius: 7,
              border: "none",
              cursor: "pointer",
              fontSize: "0.75rem",
              fontWeight: 600,
              backgroundColor: C.mid,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            <FontAwesomeIcon icon={faSearch} style={{ fontSize: "0.6rem" }} />
            Search
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Reference Material: static data ───────────────────────────────────────── */
const CROP_EMOJI_MAP = {
  Tomato: "🍅",
  Tomatoes: "🍅",
  Wheat: "🌾",
  Cotton: "🪴",
  Sugarcane: "🎋",
  Onion: "🧅",
  Soybean: "🫘",
  Gram: "🫘",
  Moong: "🫘",
  Mango: "🥭",
  Rice: "🍚",
  Maize: "🌽",
  Corn: "🌽",
  Potato: "🥔",
  Grapes: "🍇",
  Pomegranate: "🍎",
  "Cole Crops": "🥦",
  Groundnut: "🥜",
  Chilli: "🌶️",
  Turmeric: "🌿",
  Sorghum: "🌾",
  Bajra: "🌾",
  Jowar: "🌾",
};
const getCropEmoji = (name) => CROP_EMOJI_MAP[name] || "🌱";

/* ─── Crop image map ─────────────────────────────────────────────────────────── */
const CROP_IMAGE_MAP = {
  // Grains
  Wheat: require("../../assets/images/crops/wheat.png"),
  Rice: require("../../assets/images/crops/rice.png"),
  Maize: require("../../assets/images/crops/maize.png"),
  Corn: require("../../assets/images/crops/corn.png"),
  Sorghum: require("../../assets/images/crops/jowar.png"),
  Bajra: require("../../assets/images/crops/bajra.png"),
  // Pulses
  Gram: require("../../assets/images/crops/gram.png"),
  Soybean: require("../../assets/images/crops/soybean.png"),
  Groundnut: require("../../assets/images/crops/groundnut.png"),
  Peas: require("../../assets/images/crops/peas.png"),
  // Vegetables
  Onion: require("../../assets/images/crops/onion.png"),
  Potato: require("../../assets/images/crops/potato.png"),
  Chilli: require("../../assets/images/crops/chilli.png"),
  Tomato: require("../../assets/images/crops/tomato.png"),
  Brinjal: require("../../assets/images/crops/brinjal.png"),
  Okra: require("../../assets/images/crops/okra.png"),
  Cabbage: require("../../assets/images/crops/cabbage.png"),
  Cauliflower: require("../../assets/images/crops/cauliflower.png"),
  "Cole Crops": require("../../assets/images/crops/cauliflower.png"),
  Capsicum: require("../../assets/images/crops/capsicum.png"),
  Coriander: require("../../assets/images/crops/coriander.png"),
  Ginger: require("../../assets/images/crops/ginger.png"),
  Turmeric: require("../../assets/images/crops/turmeric.png"),
  Garlic: require("../../assets/images/crops/garlic.png"),
  "Bitter Gourd": require("../../assets/images/crops/bittergourd.png"),
  "Bottle Gourd": require("../../assets/images/crops/bottlegourd.png"),
  Pumpkin: require("../../assets/images/crops/pumpkin.png"),
  // Fruits
  Mango: require("../../assets/images/crops/mango.png"),
  Grapes: require("../../assets/images/crops/grapes.png"),
  Pomegranate: require("../../assets/images/crops/pomegranate.png"),
  Guava: require("../../assets/images/crops/guava.png"),
  Papaya: require("../../assets/images/crops/papaya.png"),
  Orange: require("../../assets/images/crops/orange.png"),
  Watermelon: require("../../assets/images/crops/watermelon.png"),
  Muskmelon: require("../../assets/images/crops/muskmelon.png"),
  Coconut: require("../../assets/images/crops/coconut.png"),
  Avocado: require("../../assets/images/crops/avocado.png"),
  "Custard Apple": require("../../assets/images/crops/custardapple.png"),
  Hibiscus: require("../../assets/images/crops/hibiscus.png"),
  // Fibre / Cash
  Cotton: require("../../assets/images/crops/cotton.png"),
  "Dragon Fruit": require("../../assets/images/crops/dragonfruit.png"),
  Strawberry: require("../../assets/images/crops/strawberry.png"),
  Beans: require("../../assets/images/crops/beans.png"),
  Chrysanthemum: require("../../assets/images/crops/chrysanthemum.png"),
  // Grains
  Paddy: require("../../assets/images/crops/paddy.png"),
  "Finger Millet(Ragi)": require("../../assets/images/crops/ragi.png"),
  // Pulses
  "Black Gram": require("../../assets/images/crops/blackgram.png"),
  "Green Gram": require("../../assets/images/crops/greengram.png"),
  "Red Gram": require("../../assets/images/crops/redgram.png"),
  Cowpea: require("../../assets/images/crops/cowpea.png"),
  "Cluster Bean (Guar)": require("../../assets/images/crops/guar.png"),
  Linseed: require("../../assets/images/crops/linseed.png"),
  // Vegetables
  Carrot: require("../../assets/images/crops/carrot.png"),
  Beetroot: require("../../assets/images/crops/beetroot.png"),
  Radish: require("../../assets/images/crops/radish.png"),
  Spinach: require("../../assets/images/crops/spinach.png"),
  Cucumber: require("../../assets/images/crops/cucumber.png"),
  Pea: require("../../assets/images/crops/pea.png"),
  "Sweet Potato": require("../../assets/images/crops/sweetpotato.png"),
  Drumstick: require("../../assets/images/crops/drumstick.png"),
  Brocoli: require("../../assets/images/crops/brocoli.png"),
  Fenugreek: require("../../assets/images/crops/fenugreek.png"),
  Cumin: require("../../assets/images/crops/cumin.png"),
  "Ivy Gourd": require("../../assets/images/crops/ivygourd.png"),
  "Ridge Gourd": require("../../assets/images/crops/ridgegourd.png"),
  Zucchini: require("../../assets/images/crops/zucchini.png"),
  // Fruits & Nuts
  Apple: require("../../assets/images/crops/apple.png"),
  "Apple Ber": require("../../assets/images/crops/appleber.png"),
  Banana: require("../../assets/images/crops/banana.png"),
  Lemon: require("../../assets/images/crops/lemon.png"),
  Litchi: require("../../assets/images/crops/litchi.png"),
  Jamun: require("../../assets/images/crops/jamun.png"),
  Fig: require("../../assets/images/crops/fig.png"),
  Jackfruit: require("../../assets/images/crops/jackfruit.png"),
  Sapota: require("../../assets/images/crops/sapota.png"),
  Arecanut: require("../../assets/images/crops/arecanut.png"),
  Almond: require("../../assets/images/crops/almond.png"),
  Cashew: require("../../assets/images/crops/cashew.png"),
  Walnut: require("../../assets/images/crops/walnut.png"),
  // Flowers
  Rose: require("../../assets/images/crops/rose.png"),
  Marigold: require("../../assets/images/crops/marigold.png"),
  Gerbera: require("../../assets/images/crops/gerbera.png"),
  Tuberose: require("../../assets/images/crops/tuberose.png"),
  // Spices / Cash / Other
  Mustard: require("../../assets/images/crops/mustard.png"),
  Sesame: require("../../assets/images/crops/sesame.png"),
  Castor: require("../../assets/images/crops/castor.png"),
  Niger: require("../../assets/images/crops/niger.png"),
  Sunflower: require("../../assets/images/crops/sunflower.png"),
  Sugarcane: require("../../assets/images/crops/sugarcane.png"),
  Tea: require("../../assets/images/crops/tea.png"),
  Coffe: require("../../assets/images/crops/coffe.png"),
  Opium: require("../../assets/images/crops/opium.png"),
  Pineapple: require("../../assets/images/crops/pineapple.png"),
  Rajma: require("../../assets/images/crops/rajma.png"),
  Bamboo: require("../../assets/images/crops/bamboo.png"),
  Berseem: require("../../assets/images/crops/berseem.png"),
  "Napier grass": require("../../assets/images/crops/napiergrass.png"),
  "Sweet Corn": require("../../assets/images/crops/babaycorn.png"),
  "Ash Gourd": require("../../assets/images/crops/ashgourd.png"),
  Ber: require("../../assets/images/crops/ber.png"),
  Cocoa: require("../../assets/images/crops/cocoa.png"),
  Lentil: require("../../assets/images/crops/lentil.png"),
  "Napier Grass": require("../../assets/images/crops/napiergrass.png"),
  "Pigeon Pea": require("../../assets/images/crops/pigeanpea.png"),
  Safflower: require("../../assets/images/crops/safflower.jpg"),
  "Sweet Lime": require("../../assets/images/crops/sweetlime.png"),
  Tobacco: require("../../assets/images/crops/tobacco.png"),
  Barley: require("../../assets/images/crops/barley.png"),
};
const getCropImage = (name) => {
  const m = CROP_IMAGE_MAP[name];
  if (!m) return null;
  return m.default || m;
};

const CAT_EMOJI_MAP = {
  Fungal: "🍄",
  Viral: "🦠",
  Bacterial: "🔬",
  "Insect / Pest": "🐛",
  "Insect/Pest": "🐛",
  Disorders: "⚠️",
  "Nutrient Deficiency": "🧪",
  Nematodes: "🪱",
  Abiotic: "☀️",
};
const getCatEmoji = (cat) => CAT_EMOJI_MAP[cat] || "🔬";

const PROBLEM_DETAILS = {
  "Early Blight": {
    description:
      "Brown spots with concentric rings on lower leaves, spreading upward and causing defoliation.",
    causes: [
      "Fungal infection (Alternaria solani)",
      "High humidity & wet conditions",
      "Nutrient-stressed plants",
    ],
    symptoms: [
      "Dark brown circular spots with yellow halos",
      "Concentric ring pattern inside spots",
      "Premature leaf drop",
    ],
    solution: { type: "Foliar Spray", product: "Mancozeb 75 WP" },
    dosage: [
      "2.5 g per liter of water",
      "Spray every 7–10 days",
      "3–4 sprays per season",
    ],
    application: [
      "Spray on both leaf surfaces",
      "Apply in early morning or evening",
      "Avoid spraying during rain",
    ],
  },
  "Late Blight": {
    description:
      "Water-soaked lesions on leaves and stems that turn brown with white fungal growth on undersides.",
    causes: [
      "Phytophthora infestans (fungal-like pathogen)",
      "Cool, wet & cloudy weather",
      "Poor air circulation in canopy",
    ],
    symptoms: [
      "Water-soaked spots turning brown rapidly",
      "White mold on leaf undersides",
      "Dark brown lesions on stems",
    ],
    solution: {
      type: "Preventive Spray",
      product: "Metalaxyl + Mancozeb 72 WP",
    },
    dosage: ["3 g per liter of water", "Spray every 7 days preventively"],
    application: [
      "Apply before rain forecast",
      "Ensure thorough plant coverage",
      "Alternate with Cymoxanil",
    ],
  },
  "Powdery Mildew": {
    description:
      "White powdery coating on leaves, stems and fruits reducing photosynthesis and weakening the plant.",
    causes: [
      "Fungal infection (Erysiphe/Podosphaera spp.)",
      "Dry weather with high relative humidity",
      "Dense planting & poor ventilation",
    ],
    symptoms: [
      "White powder on leaf surface",
      "Yellowing and curling of leaves",
      "Stunted plant growth",
    ],
    solution: {
      type: "Foliar Spray",
      product: "Sulphur 80 WP or Hexaconazole 5 EC",
    },
    dosage: [
      "Sulphur: 3 g/L of water",
      "Hexaconazole: 1 ml/L of water",
      "2–3 sprays at 10-day intervals",
    ],
    application: [
      "Spray in early morning",
      "Cover leaf undersides thoroughly",
      "Do not apply sulphur above 35°C",
    ],
  },
  "Leaf Curl Virus": {
    description:
      "Viral disease causing upward curling of leaves with yellow margins and severe yield loss.",
    causes: [
      "Begomovirus transmitted by whitefly",
      "High whitefly population",
      "Nearby infected weed hosts",
    ],
    symptoms: [
      "Upward leaf curling with yellow margins",
      "Thick, leathery, brittle leaves",
      "Stunted growth & poor fruit set",
    ],
    solution: {
      type: "Vector Control",
      product: "Imidacloprid 17.8 SL + Yellow Sticky Traps",
    },
    dosage: ["0.5 ml Imidacloprid per liter of water", "Spray every 15 days"],
    application: [
      "Target leaf undersides where whitefly feed",
      "Remove and destroy infected plants",
      "Plant border crops to trap pests",
    ],
  },
  "Bacterial Wilt": {
    description:
      "Sudden wilting of entire plants due to bacterial blockage of water-conducting vascular tissue.",
    causes: [
      "Ralstonia solanacearum bacteria",
      "Contaminated soil or irrigation water",
      "High soil moisture & warm temperatures",
    ],
    symptoms: [
      "Sudden wilting without prior yellowing",
      "Brown ring in stem cross-section",
      "Bacterial ooze test: milky stream in water",
    ],
    solution: {
      type: "Soil Drench (Preventive)",
      product: "Copper Oxychloride 50 WP",
    },
    dosage: ["3 g per liter of water", "Drench at base of plants"],
    application: [
      "No cure — uproot infected plants immediately",
      "Drench nearby plants as protection",
      "Avoid waterlogging; improve drainage",
    ],
  },
  "Blossom End Rot": {
    description:
      "Dark brown or black necrotic spot at the blossom end of fruit, progressing inward.",
    causes: [
      "Calcium deficiency in developing fruit",
      "Irregular irrigation causing Ca uptake failure",
      "Excess nitrogen fertilizer",
    ],
    symptoms: [
      "Dark spot at fruit bottom",
      "Firm, leathery patch on fruit tip",
      "Spot enlarges as fruit grows",
    ],
    solution: { type: "Foliar Spray", product: "Calcium Nitrate" },
    dosage: [
      "2–3 gram per liter of water",
      "Spray weekly during fruit development",
    ],
    application: [
      "Spray on leaves and developing fruits",
      "Maintain consistent drip irrigation",
      "Reduce ammoniacal nitrogen use",
    ],
  },
  Nematodes: {
    description:
      "Microscopic roundworms in soil causing root galls, stunted growth and nutrient deficiency symptoms.",
    causes: [
      "Root-knot nematode (Meloidogyne spp.)",
      "Infested soil or planting material",
      "Sandy, warm soil conditions",
    ],
    symptoms: [
      "Swollen root galls (knots)",
      "Stunted and yellowing plants",
      "Wilting despite adequate irrigation",
    ],
    solution: { type: "Soil Treatment", product: "Carbofuran 3G or Neem Cake" },
    dosage: ["Carbofuran: 1 kg per 500 sq m", "Neem cake: 250 kg/acre"],
    application: [
      "Apply before planting & mix into soil",
      "Follow with irrigation",
      "Use resistant varieties where available",
    ],
  },
  Yellowing: {
    description:
      "Interveinal or whole-leaf yellowing indicating nutrient deficiency or pathogen infection.",
    causes: [
      "Iron or Nitrogen deficiency",
      "Magnesium deficiency in sandy soils",
      "Root damage reducing nutrient uptake",
    ],
    symptoms: [
      "Pale yellow leaves (younger or older depending on nutrient)",
      "Green veins with yellow interveinal tissue",
      "Reduced growth",
    ],
    solution: {
      type: "Foliar Spray + Soil Application",
      product: "Ferrous Sulphate / Urea",
    },
    dosage: ["FeSO₄: 5 g/L with citric acid 1 g/L", "Urea: 1% foliar spray"],
    application: [
      "Spray 2–3 times at weekly intervals",
      "Test soil pH — correct if >7.5 for Fe deficiency",
    ],
  },
};

const getGenericDetail = (problemText, catName) => ({
  description: `${problemText} is a ${catName || "common"} problem affecting crop health and yield. Early identification and timely management are critical.`,
  causes: [
    "Pathogen or environmental stress",
    "Favorable weather conditions for disease",
    "Susceptible crop variety",
  ],
  symptoms: [
    "Visible damage on leaves, stem or fruit",
    "Reduced growth and vigor",
    "Potential yield loss if untreated",
  ],
  solution: {
    type: "Consult agronomist",
    product: "Specific product recommendation required",
  },
  dosage: ["Follow recommended product label", "Apply at prescribed intervals"],
  application: [
    "Apply in early morning or evening",
    "Ensure complete plant coverage",
  ],
});

const getLocalProblemFallback = (problemText, catName) =>
  PROBLEM_DETAILS[problemText] || getGenericDetail(problemText, catName);

/* ─── Reference Material Drawer ─────────────────────────────────────────────── */
const APP_METHODS = [
  "Spraying",
  "Drip Irrigation",
  "Soil Application",
  "Seed Treatment",
  "Foliar Spray",
  "Fertigation",
];

function RefMaterialDrawer({
  open,
  problem,
  cropName,
  initialDetail,
  onClose,
  onSaved,
}) {
  const [mode, setMode] = useState("view");
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saveErr, setSaveErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [productDrawer, setProductDrawer] = useState(null);
  const [cropQueue, setCropQueue] = useState([]);
  const croppedBlobsRef = useRef([]);
  const cropTotalRef = useRef(0);
  const imgInputRef = useRef(null);

  useEffect(() => {
    getProducts().then((res) => {
      if (res?.status === 200) setAllProducts(res.data);
    });
  }, []);

  useEffect(() => {
    if (open && problem) {
      setMode("view");
      setSaveErr("");
      const base = initialDetail;
      const emptyDraft = {
        problemName: problem.problemText || "",
        causes: [],
        symptoms: [],
        solutions: [
          {
            name: "",
            products: [
              {
                productId: "",
                productName: "",
                dose: "",
                applicationMethod: "",
              },
            ],
          },
        ],
        images: [],
      };
      if (!base) {
        setDraft(emptyDraft);
        return;
      }
      // Migrate old format (solutionType/solutionProduct/dosage/application) to new solutions array
      const solutions =
        base.solutions ||
        (base.solutionType
          ? [
              {
                name: base.solutionType,
                products: [
                  {
                    productId: "",
                    productName: base.solutionProduct || "",
                    dose: (base.dosage || [])[0] || "",
                    applicationMethod: (base.application || [])[0] || "",
                  },
                ],
              },
            ]
          : emptyDraft.solutions);
      setDraft({
        problemName: base.problemName || problem.problemText || "",
        causes: base.causes || [],
        symptoms: base.symptoms || [],
        solutions,
        images: base.images || [],
      });
    }
  }, [open, problem, initialDetail]);

  useEffect(() => {
    if (cropQueue.length === 0 && croppedBlobsRef.current.length > 0) {
      const blobs = [...croppedBlobsRef.current];
      croppedBlobsRef.current = [];
      (async () => {
        setUploading(true);
        const newUrls = [];
        for (const blob of blobs) {
          const res = await uploadProblemImage(blob);
          if (res?.data?.imageUrl) newUrls.push(res.data.imageUrl);
        }
        setUploading(false);
        if (newUrls.length)
          setDraft((prev) => ({ ...prev, images: [...(prev.images || []), ...newUrls] }));
      })();
    }
  }, [cropQueue]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !draft) return null;

  const D = draft;
  const productOpts = (allProducts || []).map((p) => ({
    value: String(p._id || p.id),
    label: p.product || p.name || "",
  }));
  const setF = (k, v) => setDraft((p) => ({ ...p, [k]: v }));
  const setArr = (k, i, v) => {
    const a = [...(D[k] || [])];
    a[i] = v;
    setF(k, a);
  };
  const addArr = (k) => setF(k, [...(D[k] || []), ""]);
  const delArr = (k, i) =>
    setF(
      k,
      (D[k] || []).filter((_, j) => j !== i),
    );

  const addSolution = () =>
    setF("solutions", [
      ...(D.solutions || []),
      {
        name: "",
        products: [
          { productId: "", productName: "", dose: "", applicationMethod: "" },
        ],
      },
    ]);
  const removeSolution = (si) =>
    setF(
      "solutions",
      (D.solutions || []).filter((_, j) => j !== si),
    );
  const setSolName = (si, v) => {
    const s = [...(D.solutions || [])];
    s[si] = { ...s[si], name: v };
    setF("solutions", s);
  };
  const addProduct = (si) => {
    const s = [...(D.solutions || [])];
    s[si] = {
      ...s[si],
      products: [
        ...(s[si].products || []),
        { productId: "", productName: "", dose: "", applicationMethod: "" },
      ],
    };
    setF("solutions", s);
  };
  const removeProduct = (si, pi) => {
    const s = [...(D.solutions || [])];
    s[si] = {
      ...s[si],
      products: (s[si].products || []).filter((_, j) => j !== pi),
    };
    setF("solutions", s);
  };
  const setProduct = (si, pi, field, val) => {
    const s = [...(D.solutions || [])];
    const ps = [...(s[si].products || [])];
    ps[pi] = { ...ps[pi], [field]: val };
    s[si] = { ...s[si], products: ps };
    setF("solutions", s);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const oversized = files.filter((f) => f.size > 5 * 1024 * 1024);
    if (oversized.length)
      setSaveErr(
        `${oversized.length} file(s) exceed the 5 MB limit and were skipped.`,
      );
    const valid = files.filter((f) => f.size <= 5 * 1024 * 1024);
    if (!valid.length) return;
    croppedBlobsRef.current = [];
    cropTotalRef.current = valid.length;
    setCropQueue(valid);
  };

  const handleCropComplete = (blob) => {
    croppedBlobsRef.current.push(blob);
    setCropQueue((prev) => prev.slice(1));
  };

  const handleCropCancel = () => {
    setCropQueue((prev) => prev.slice(1));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveErr("");
    const res = await saveProblemDetail({ problemId: problem.id, ...draft });
    setSaving(false);
    if (res?.status === 200) {
      setMode("view");
      if (onSaved) onSaved(draft);
    } else setSaveErr("Failed to save. Please try again.");
  };

  const inp = {
    width: "100%",
    borderRadius: 7,
    border: `1.5px solid ${C.border}`,
    padding: "0.42rem 0.65rem",
    fontSize: "0.85rem",
    color: C.textDk,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    backgroundColor: "#fff",
  };
  const sel = { ...inp, cursor: "pointer" };
  const secTitle = {
    fontSize: "0.92rem",
    fontWeight: 800,
    color: "#166534",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: "0.75rem",
  };
  const secBox = {
    border: `1.5px solid ${C.border}`,
    borderRadius: 10,
    padding: "1rem 1.1rem",
    marginBottom: "1rem",
    backgroundColor: "#fff",
  };
  const delBtn = {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: "none",
    backgroundColor: "#fdecea",
    color: C.red,
    cursor: "pointer",
    flexShrink: 0,
    fontSize: "0.8rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  const dashBtn = (label, onClick) => (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        height: 36,
        borderRadius: 8,
        border: `1.5px dashed ${C.border}`,
        backgroundColor: "#f8faf9",
        color: C.mid,
        fontSize: "0.82rem",
        fontWeight: 600,
        cursor: "pointer",
        marginTop: 8,
      }}
    >
      {label}
    </button>
  );

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1050,
          backgroundColor: "rgba(0,0,0,0.42)",
        }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 860,
          maxWidth: "96vw",
          zIndex: 1060,
          backgroundColor: "#f8faf9",
          display: "flex",
          flexDirection: "column",
          boxShadow: "-6px 0 32px rgba(0,0,0,0.22)",
          animation: "refSlideIn 0.28s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: C.dark,
            padding: "1rem 1.25rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: "1.8rem", lineHeight: 1 }}>
              {getCatEmoji(problem.catName)}
            </span>
            <div>
              <div style={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>
                {problem.problemText}
              </div>
              <div
                style={{
                  fontSize: "0.72rem",
                  color: "rgba(255,255,255,0.58)",
                  marginTop: 2,
                }}
              >
                {problem.catName} · {cropName}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {mode === "view" ? (
              <button
                onClick={() => setMode("edit")}
                style={{
                  height: 34,
                  padding: "0 14px",
                  borderRadius: 8,
                  border: "1.5px solid rgba(255,255,255,0.28)",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: "#fff",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <FontAwesomeIcon icon={faPen} style={{ fontSize: "0.68rem" }} />{" "}
                Edit
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    height: 34,
                    padding: "0 14px",
                    borderRadius: 8,
                    border: "none",
                    backgroundColor: C.bright,
                    color: "#fff",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    cursor: saving ? "not-allowed" : "pointer",
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={() => setMode("view")}
                  style={{
                    height: 34,
                    padding: "0 12px",
                    borderRadius: 8,
                    border: "1.5px solid rgba(255,255,255,0.28)",
                    backgroundColor: "transparent",
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "0.8rem",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </>
            )}
            <button
              onClick={onClose}
              style={{
                width: 32,
                height: 32,
                borderRadius: 7,
                background: "rgba(255,255,255,0.12)",
                border: "none",
                cursor: "pointer",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: "0.85rem" }} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "1.5rem" }}>
          {saveErr && (
            <div
              style={{
                padding: "0.55rem 0.85rem",
                borderRadius: 8,
                backgroundColor: C.redBg,
                color: C.red,
                fontSize: "0.8rem",
                marginBottom: "1rem",
              }}
            >
              {saveErr}
            </div>
          )}

          {/* 1. Problem Name */}
          <div style={secBox}>
            <div style={secTitle}>1. Problem Name</div>
            {mode === "view" ? (
              <div
                style={{ fontSize: "1rem", fontWeight: 600, color: C.textDk }}
              >
                {D.problemName || problem.problemText}
              </div>
            ) : (
              <input
                style={inp}
                value={D.problemName || ""}
                onChange={(e) => setF("problemName", e.target.value)}
                placeholder="Enter problem name"
              />
            )}
          </div>

          {/* 2 & 3: Causes + Symptoms */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
              marginBottom: "1rem",
            }}
          >
            {[
              ["causes", "2. Causes"],
              ["symptoms", "3. Symptoms"],
            ].map(([key, label]) => (
              <div
                key={key}
                style={{
                  border: `1.5px solid ${C.border}`,
                  borderRadius: 10,
                  padding: "1rem 1.1rem",
                  backgroundColor: "#fff",
                }}
              >
                <div style={secTitle}>{label}</div>
                {(D[key] || []).length === 0 && mode === "view" && (
                  <div style={{ fontSize: "0.83rem", color: C.textGr }}>
                    No {key} recorded.
                  </div>
                )}
                {(D[key] || []).map((val, i) =>
                  mode === "view" ? (
                    <div
                      key={i}
                      style={{ display: "flex", gap: 8, marginBottom: 6 }}
                    >
                      <span style={{ color: C.mid, fontWeight: 700 }}>•</span>
                      <span style={{ fontSize: "0.87rem", color: C.textDk }}>
                        {val}
                      </span>
                    </div>
                  ) : (
                    <div
                      key={i}
                      style={{ display: "flex", gap: 6, marginBottom: 8 }}
                    >
                      <input
                        style={{ ...inp, flex: 1 }}
                        value={val}
                        onChange={(e) => setArr(key, i, e.target.value)}
                        placeholder={`Enter ${key.slice(0, -1)}`}
                      />
                      <button style={delBtn} onClick={() => delArr(key, i)}>
                        ×
                      </button>
                    </div>
                  ),
                )}
                {mode === "edit" &&
                  dashBtn(
                    `+ Add ${key === "causes" ? "Cause" : "Symptom"}`,
                    () => addArr(key),
                  )}
              </div>
            ))}
          </div>

          {/* 4. Solutions */}
          <div
            style={{
              border: `1.5px solid ${C.border}`,
              borderRadius: 10,
              padding: "1rem 1.1rem",
              marginBottom: "1rem",
              backgroundColor: "#f8faf9",
            }}
          >
            <div style={secTitle}>4. Solutions</div>
            {(D.solutions || []).map((sol, si) => (
              <div
                key={si}
                style={{
                  border: `1.5px solid ${C.border}`,
                  borderRadius: 10,
                  backgroundColor: "#fff",
                  marginBottom: "1rem",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0.65rem 1rem",
                    borderBottom: `1px solid ${C.border}`,
                    backgroundColor: "#f0f9f4",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: "0.88rem",
                      color: "#166534",
                    }}
                  >
                    Solution {si + 1}
                  </span>
                  {mode === "edit" && (D.solutions || []).length > 1 && (
                    <button
                      onClick={() => removeSolution(si)}
                      style={{
                        background: "none",
                        border: "none",
                        color: C.red,
                        cursor: "pointer",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faTrash}
                        style={{ fontSize: "0.7rem" }}
                      />{" "}
                      Remove Solution
                    </button>
                  )}
                </div>
                <div style={{ padding: "1rem" }}>
                  <div style={{ marginBottom: "0.85rem" }}>
                    <label
                      style={{
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        color: C.textGr,
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Solution Name
                    </label>
                    {mode === "view" ? (
                      <div
                        style={{
                          fontSize: "0.9rem",
                          fontWeight: 600,
                          color: C.textDk,
                        }}
                      >
                        {sol.name || "—"}
                      </div>
                    ) : (
                      <input
                        style={{ ...inp, maxWidth: 340 }}
                        value={sol.name || ""}
                        onChange={(e) => setSolName(si, e.target.value)}
                        placeholder="e.g. Preventive Solution"
                      />
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      color: C.textGr,
                      marginBottom: 6,
                    }}
                  >
                    Products
                  </div>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "0.83rem",
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: C.dark, color: "#fff" }}>
                        <th
                          style={{
                            padding: "0.5rem 0.6rem",
                            width: 36,
                            textAlign: "center",
                            fontWeight: 600,
                          }}
                        >
                          #
                        </th>
                        <th
                          style={{ padding: "0.5rem 0.6rem", fontWeight: 600 }}
                        >
                          Product Name
                        </th>
                        <th
                          style={{ padding: "0.5rem 0.6rem", fontWeight: 600 }}
                        >
                          Dose
                        </th>
                        <th
                          style={{ padding: "0.5rem 0.6rem", fontWeight: 600 }}
                        >
                          Application Method
                        </th>
                        {mode === "edit" && (
                          <th
                            style={{
                              padding: "0.5rem 0.6rem",
                              textAlign: "center",
                              fontWeight: 600,
                            }}
                          >
                            Action
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {(sol.products || []).map((prod, pi) => (
                        <tr
                          key={pi}
                          style={{
                            borderBottom: `1px solid ${C.border}`,
                            backgroundColor: pi % 2 === 0 ? "#fff" : "#f8faf9",
                          }}
                        >
                          <td
                            style={{
                              padding: "0.45rem 0.6rem",
                              textAlign: "center",
                              color: C.textGr,
                            }}
                          >
                            {pi + 1}
                          </td>
                          <td style={{ padding: "0.35rem 0.45rem" }}>
                            {mode === "view" ? (
                              prod.productId ? (
                                <span
                                  onClick={() => {
                                    const p = allProducts.find(
                                      (x) =>
                                        String(x._id || x.id) ===
                                        prod.productId,
                                    );
                                    setProductDrawer(
                                      p || {
                                        _id: prod.productId,
                                        product: prod.productName,
                                      },
                                    );
                                  }}
                                  style={{
                                    color: "#166534",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                    textUnderlineOffset: 2,
                                  }}
                                >
                                  {prod.productName || "—"}
                                </span>
                              ) : (
                                <span>{prod.productName || "—"}</span>
                              )
                            ) : (
                              <Select
                                options={productOpts}
                                value={
                                  productOpts.find(
                                    (o) => o.value === String(prod.productId),
                                  ) ||
                                  (prod.productName
                                    ? { value: prod.productId, label: prod.productName }
                                    : null)
                                }
                                onChange={(opt) => {
                                  const s = [...(D.solutions || [])];
                                  const ps = [...(s[si].products || [])];
                                  ps[pi] = {
                                    ...ps[pi],
                                    productId: opt?.value || "",
                                    productName: opt?.label || "",
                                  };
                                  s[si] = { ...s[si], products: ps };
                                  setF("solutions", s);
                                }}
                                placeholder="Select product"
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={productSelectSt}
                              />
                            )}
                          </td>
                          <td style={{ padding: "0.35rem 0.45rem" }}>
                            {mode === "view" ? (
                              <span>{prod.dose || "—"}</span>
                            ) : (
                              <input
                                style={{ ...inp, fontSize: "0.82rem" }}
                                value={prod.dose || ""}
                                onChange={(e) =>
                                  setProduct(si, pi, "dose", e.target.value)
                                }
                                placeholder="e.g. 2 ml / L"
                              />
                            )}
                          </td>
                          <td style={{ padding: "0.35rem 0.45rem" }}>
                            {mode === "view" ? (
                              <span>{prod.applicationMethod || "—"}</span>
                            ) : (
                              <select
                                style={{ ...sel, fontSize: "0.82rem" }}
                                value={prod.applicationMethod || ""}
                                onChange={(e) =>
                                  setProduct(
                                    si,
                                    pi,
                                    "applicationMethod",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">Select method</option>
                                {APP_METHODS.map((m) => (
                                  <option key={m} value={m}>
                                    {m}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          {mode === "edit" && (
                            <td
                              style={{
                                padding: "0.35rem 0.45rem",
                                textAlign: "center",
                              }}
                            >
                              <button
                                onClick={() => removeProduct(si, pi)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: C.red,
                                  cursor: "pointer",
                                  fontSize: "1rem",
                                  lineHeight: 1,
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  style={{ fontSize: "0.8rem" }}
                                />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {mode === "edit" &&
                    dashBtn("+ Add Product", () => addProduct(si))}
                </div>
              </div>
            ))}
            {mode === "edit" && (
              <button
                onClick={addSolution}
                style={{
                  width: "100%",
                  height: 40,
                  borderRadius: 9,
                  border: `1.5px solid ${C.mid}`,
                  backgroundColor: "#fff",
                  color: C.mid,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                + Add Another Solution
              </button>
            )}
          </div>

          {/* 5. Photo */}
          <div style={secBox}>
            <div style={secTitle}>5. Photo</div>
            {mode === "edit" && (
              <p
                style={{
                  fontSize: "0.78rem",
                  color: C.textGr,
                  marginBottom: "0.75rem",
                }}
              >
                Upload problem related photos (You can upload multiple images)
              </p>
            )}
            <div
              style={{
                display: "flex",
                gap: "1rem",
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              {mode === "edit" && (
                <div
                  onClick={() => !uploading && imgInputRef.current?.click()}
                  style={{
                    width: 140,
                    height: 120,
                    borderRadius: 10,
                    border: `2px dashed ${C.border}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: uploading ? "not-allowed" : "pointer",
                    backgroundColor: "#f8faf9",
                    flexShrink: 0,
                    gap: 2,
                  }}
                >
                  <span style={{ fontSize: "2rem", color: C.mid }}>☁</span>
                  <span
                    style={{
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      color: C.mid,
                    }}
                  >
                    {uploading ? "Uploading…" : "Click to upload"}
                  </span>
                  {!uploading && (
                    <>
                      <span style={{ fontSize: "0.7rem", color: C.textGr }}>
                        or drag and drop
                      </span>
                      <span style={{ fontSize: "0.65rem", color: C.textGr }}>
                        JPG, PNG (Max. 5 MB each)
                      </span>
                    </>
                  )}
                  <input
                    ref={imgInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleImageUpload}
                  />
                </div>
              )}
              {(D.images || []).length === 0 && mode === "view" && (
                <div style={{ fontSize: "0.85rem", color: C.textGr }}>
                  No photos uploaded.
                </div>
              )}
              {(D.images || []).map((img, i) => (
                <div
                  key={i}
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: 10,
                    overflow: "hidden",
                    border: `1px solid ${C.border}`,
                    position: "relative",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={img}
                    alt=""
                    onClick={() => {
                      if (mode !== "edit") setLightboxUrl(img);
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      cursor: mode !== "edit" ? "zoom-in" : "default",
                    }}
                  />
                  {mode === "edit" && (
                    <button
                      onClick={() => delArr("images", i)}
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        border: "2px solid #fff",
                        backgroundColor: C.red,
                        color: "#fff",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes refSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
      <ProductDetailDrawer
        open={!!productDrawer}
        product={productDrawer}
        onClose={() => setProductDrawer(null)}
        onSaved={() => setProductDrawer(null)}
      />
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 3000,
            backgroundColor: "rgba(0,0,0,0.88)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <img
            src={lightboxUrl}
            alt="Preview"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              borderRadius: 10,
              boxShadow: "0 8px 48px rgba(0,0,0,0.6)",
              objectFit: "contain",
              cursor: "default",
            }}
          />
          <button
            onClick={() => setLightboxUrl(null)}
            style={{
              position: "fixed",
              top: 18,
              right: 22,
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "50%",
              width: 38,
              height: 38,
              color: "#fff",
              fontSize: "1.1rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      )}
      {cropQueue.length > 0 && (
        <ImageCropModal
          imageFile={cropQueue[0]}
          aspectRatio={4 / 3}
          title="Crop Photo"
          stepLabel={
            cropTotalRef.current > 1
              ? `${cropTotalRef.current - cropQueue.length + 1} of ${cropTotalRef.current}`
              : undefined
          }
          onComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </>
  );
}

export default function Crop() {
  const location = useLocation();

  /* Crops */
  const [crops, setCrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cropSearch, setCropSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const firstLoad = useRef(true);

  /* Page tabs: "crops" | "issues" | "reference" */
  const [pageTab, setPageTab] = useState(
    location.state?.restoreTab || "reference",
  );

  /* Issues tab */
  const [pageIssues, setPageIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesLoaded, setIssuesLoaded] = useState(false);
  const [isDefaultView, setIsDefaultView] = useState(true); // true = show top 10, false = search results
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;
  const [allUsers, setAllUsers] = useState([]);
  const [allCropsFlat, setAllCropsFlat] = useState([]);

  /* Drawers */
  const [cropDrawer, setCropDrawer] = useState(null); // null | "add" | "edit"
  const [issueDrawer, setIssueDrawer] = useState(false);

  /* Reference Material tab */
  const [refCrop, setRefCrop] = useState(null);
  const [refProblem, setRefProblem] = useState(null); // { problemText, catName, id }
  const [refSearch, setRefSearch] = useState("");
  const [refDrawerOpen, setRefDrawerOpen] = useState(false);
  const [refDetailData, setRefDetailData] = useState(null); // fetched from API
  const [refDetailLoading, setRefDetailLoading] = useState(false);

  /* ── Load crops ── */
  const loadCrops = useCallback(() => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/crop/allCrops`)
      .then((res) => {
        if (res.status === 200) {
          setCrops(res.data);
          // Auto-select first crop on initial load only
          if (firstLoad.current && res.data.length > 0) {
            setSelected(res.data[0]);
            firstLoad.current = false;
          }
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadCrops();
  }, [loadCrops]);

  /* ── Load reference data for search ── */
  useEffect(() => {
    getAllUsers().then((r) => {
      if (r.status === 200) setAllUsers(r.data);
    });
    getAllCrops().then((r) => {
      if (r.status === 200) setAllCropsFlat(r.data);
    });
  }, []);

  /* ── Load issues ──
     Default load: /api/reports/issueList with {} returns all issues { success, data: [] }.
     Search: /api/issue/searchIssue requires at least one filter.
  */
  const loadIssues = useCallback((filters = null) => {
    setIssuesLoading(true);
    if (filters) {
      // User triggered search — use searchIssue with explicit filters
      axios
        .post(`${process.env.REACT_APP_API_URL}/api/issue/searchIssue`, filters)
        .then((res) => {
          const raw = res?.data;
          const list = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.issues)
              ? raw.issues
              : [];
          setPageIssues(list);
          setIssuesLoading(false);
          setIssuesLoaded(true);
        })
        .catch(() => {
          setIssuesLoading(false);
          setIssuesLoaded(true);
        });
    } else {
      // Default load — pass required params as null to get all latest queries
      axios
        .post(`${process.env.REACT_APP_API_URL}/api/issue/searchIssue`, {
          issueCode: null,
          reporter: null,
          assignee: null,
        })
        .then((res) => {
          const raw = res?.data;
          const list = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.issues)
              ? raw.issues
              : Array.isArray(raw?.data)
                ? raw.data
                : [];
          setPageIssues(list);
          setIssuesLoading(false);
          setIssuesLoaded(true);
        })
        .catch(() => {
          setIssuesLoading(false);
          setIssuesLoaded(true);
        });
    }
  }, []);

  useEffect(() => {
    loadIssues(null);
  }, [loadIssues]);

  const onPageTabChange = (tab) => {
    setPageTab(tab);
    if (tab === "issues") {
      setIsDefaultView(true);
      setCurrentPage(1);
      loadIssues(null);
    }
    if (tab === "crops") {
      loadCrops();
    }
  };

  const onSearch = (filters) => {
    setIsDefaultView(false);
    setCurrentPage(1);
    loadIssues(filters);
  };
  const onSearchReset = () => {
    setIsDefaultView(true);
    setCurrentPage(1);
    loadIssues(null);
  };

  /* ── Crop handlers ── */
  const onSelectCrop = (crop) => setSelected(crop);

  // opts.close (default true) — pass { close: false } from category/problem
  // add or delete actions so the drawer stays open for the next action instead
  // of forcing the user to reopen it.
  const onCropSuccess = (updatedCrop, opts = {}) => {
    const shouldClose = opts.close !== false;
    if (shouldClose) setCropDrawer(null);
    if (!updatedCrop) {
      // Crop was deleted — clear selection
      setSelected(null);
    } else {
      // updatedCrop is always the full, server-confirmed crop tree — use it as-is
      // rather than merging, so a leftover previous selection can't bleed through.
      setSelected(updatedCrop);
    }
    loadCrops();
  };

  const onIssueSuccess = () => {
    setIssueDrawer(false);
    loadIssues({});
    setPageTab("issues");
  };

  const filtered = crops.filter((c) =>
    c.crop?.toLowerCase().includes(cropSearch.toLowerCase()),
  );

  /* ─── Render ──────────────────────────────────────────────────────────────── */
  return (
    <div
      style={{
        backgroundColor: C.page,
        height: "calc(100vh - 80px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ── Page Header ─────────────────────────────────────────────────────── */}
      <div
        style={{
          backgroundColor: C.white,
          padding: "0.85rem 1.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${C.border}`,
          boxShadow: "0 1px 4px rgba(20,50,30,0.06)",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              backgroundColor: C.light,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FontAwesomeIcon
              icon={faSeedling}
              style={{ color: C.mid, fontSize: "0.9rem" }}
            />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: "1rem", color: C.textDk }}>
              Crop Management
            </div>
            <div style={{ fontSize: "0.72rem", color: C.textGr }}>
              {loading ? "Loading…" : `${crops.length} crops registered`}
            </div>
          </div>
        </div>
      </div>

      {/* ── Page-level Tab Bar ───────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderBottom: `2px solid ${C.border}`,
          backgroundColor: C.white,
          padding: "0 1.5rem",
          flexShrink: 0,
        }}
      >
        {[
          { key: "reference", label: "Reference Material", icon: faBookOpen },
          {
            key: "issues",
            label: `Queries${pageIssues.length ? ` (${pageIssues.length})` : ""}`,
            icon: faExclamationCircle,
          },
          { key: "crops", label: "Crop Details", icon: faLeaf },
        ].map(({ key, label, icon }) => {
          const active = pageTab === key;
          return (
            <button
              key={key}
              onClick={() => onPageTabChange(key)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0.6rem 1.1rem",
                marginBottom: -2,
                display: "flex",
                alignItems: "center",
                gap: 7,
                fontSize: "0.85rem",
                fontWeight: active ? 700 : 400,
                color: active ? C.mid : C.textGr,
                borderBottom: `2px solid ${active ? C.mid : "transparent"}`,
                transition: "color 0.15s",
              }}
            >
              <FontAwesomeIcon icon={icon} style={{ fontSize: "0.72rem" }} />
              {label}
            </button>
          );
        })}

        {/* Refresh + Add Crop — shown only on Crop Details tab */}
        {pageTab === "crops" && (
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: 8,
              alignItems: "center",
              paddingLeft: 8,
            }}
          >
            <Btn
              icon={faSyncAlt}
              label="Refresh"
              onClick={loadCrops}
              small
              style={{
                backgroundColor: C.white,
                color: C.textGr,
                border: `1px solid ${C.border}`,
              }}
            />
            <Btn
              icon={faPlus}
              label="Add Crop"
              onClick={() => setCropDrawer("add")}
              small
              style={{ backgroundColor: C.mid, color: "#fff" }}
            />
          </div>
        )}
      </div>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <div
        style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}
      >
        {/* ══ Tab 1: Crop Details ══ */}
        {pageTab === "crops" && (
          <>
            {/* Left sidebar: crop list */}
            <div
              style={{
                width: 260,
                flexShrink: 0,
                backgroundColor: C.white,
                borderRight: `1px solid ${C.border}`,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Search */}
              <div
                style={{
                  padding: "0.75rem",
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                <div style={{ position: "relative" }}>
                  <FontAwesomeIcon
                    icon={faSearch}
                    style={{
                      position: "absolute",
                      left: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: C.textGr,
                      fontSize: "0.75rem",
                    }}
                  />
                  <input
                    value={cropSearch}
                    onChange={(e) => setCropSearch(e.target.value)}
                    placeholder="Search crops…"
                    style={{
                      width: "100%",
                      height: 34,
                      borderRadius: 8,
                      border: `1.5px solid ${C.border}`,
                      paddingLeft: 30,
                      fontSize: "0.83rem",
                      outline: "none",
                      boxSizing: "border-box",
                      color: C.textDk,
                    }}
                  />
                </div>
              </div>

              {/* Crop list */}
              <div style={{ flex: 1, overflowY: "auto", padding: "0.5rem" }}>
                {loading ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: C.textGr,
                      fontSize: "0.85rem",
                    }}
                  >
                    Loading…
                  </div>
                ) : filtered.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: C.textGr,
                      fontSize: "0.85rem",
                    }}
                  >
                    No crops found
                  </div>
                ) : (
                  filtered.map((crop) => {
                    const isSelected = selected?._id === crop._id;
                    return (
                      <div
                        key={crop._id}
                        onClick={() => onSelectCrop(crop)}
                        style={{
                          padding: "0.6rem 0.75rem",
                          borderRadius: 9,
                          marginBottom: 4,
                          cursor: "pointer",
                          transition: "all 0.15s",
                          backgroundColor: isSelected ? C.light : "transparent",
                          border: `1px solid ${isSelected ? C.border : "transparent"}`,
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected)
                            e.currentTarget.style.backgroundColor = "#f5faf7";
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected)
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <div
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 7,
                              flexShrink: 0,
                              backgroundColor: isSelected ? C.mid : "#eef5f1",
                              color: isSelected ? "#fff" : C.mid,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.7rem",
                            }}
                          >
                            <FontAwesomeIcon icon={faSeedling} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: "0.85rem",
                                fontWeight: isSelected ? 700 : 500,
                                color: isSelected ? C.mid : C.textDk,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {crop.crop}
                            </div>
                            <div
                              style={{ fontSize: "0.68rem", color: C.textGr }}
                            >
                              {crop.categories?.length || 0} categories
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right: Crop detail panel */}
            <div style={{ flex: 1, overflowY: "auto", padding: "1.25rem" }}>
              {!selected ? (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: C.textGr,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faSeedling}
                    style={{
                      fontSize: "3rem",
                      color: C.border,
                      marginBottom: "1rem",
                    }}
                  />
                  <div style={{ fontWeight: 600, fontSize: "1rem" }}>
                    Select a crop to view details
                  </div>
                  <div style={{ fontSize: "0.83rem", marginTop: 6 }}>
                    Click any crop from the list on the left
                  </div>
                </div>
              ) : (
                <>
                  {/* Crop info card */}
                  <div
                    style={{
                      backgroundColor: C.white,
                      borderRadius: 12,
                      border: `1px solid ${C.border}`,
                      padding: "1rem 1.25rem",
                      marginBottom: "1.1rem",
                      boxShadow: "0 2px 8px rgba(20,50,30,0.04)",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.85rem",
                    }}
                  >
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        flexShrink: 0,
                        backgroundColor: C.light,
                        color: C.mid,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.2rem",
                      }}
                    >
                      <FontAwesomeIcon icon={faSeedling} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 800,
                          fontSize: "1.1rem",
                          color: C.textDk,
                        }}
                      >
                        {selected.crop}
                      </div>
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: C.textGr,
                          marginTop: 2,
                        }}
                      >
                        {selected.categories?.length || 0} categories ·{" "}
                        {selected.categories?.reduce(
                          (s, c) => s + (c.problems?.length || 0),
                          0,
                        ) || 0}{" "}
                        problems
                      </div>
                    </div>
                    <Btn
                      icon={faPen}
                      label="Edit Crop"
                      small
                      onClick={() => setCropDrawer("edit")}
                      style={{ backgroundColor: "#e8f0ff", color: "#3b82f6" }}
                    />
                  </div>

                  {/* Categories section */}
                  <div
                    style={{
                      backgroundColor: C.white,
                      borderRadius: 12,
                      border: `1px solid ${C.border}`,
                      padding: "1rem",
                      boxShadow: "0 2px 8px rgba(20,50,30,0.04)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: C.mid,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        marginBottom: "0.85rem",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faListUl}
                        style={{ fontSize: "0.65rem" }}
                      />
                      Categories &amp; Problems
                    </div>

                    {!selected.categories ||
                    selected.categories.length === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "2.5rem",
                          color: C.textGr,
                          border: `1.5px dashed ${C.border}`,
                          borderRadius: 10,
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faLayerGroup}
                          style={{
                            fontSize: "1.8rem",
                            color: C.border,
                            marginBottom: "0.75rem",
                            display: "block",
                          }}
                        />
                        <div style={{ fontWeight: 600 }}>
                          No categories added yet
                        </div>
                        <div style={{ fontSize: "0.8rem", marginTop: 4 }}>
                          Click "Edit Crop" to add categories and problems
                        </div>
                      </div>
                    ) : (
                      selected.categories.map((cat, i) => (
                        <CategoryRow key={cat._id || i} category={cat} />
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* ══ Tab 2: Issues ══ */}
        {pageTab === "issues" &&
          (() => {
            const sortedIssues = [...pageIssues].sort(
              (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0),
            );
            // Default view: top 20 latest. Search mode: full paginated results.
            const displayIssues = isDefaultView
              ? sortedIssues.slice(0, 20)
              : sortedIssues;
            const totalPages = isDefaultView
              ? 1
              : Math.ceil(sortedIssues.length / PAGE_SIZE);
            const pagedIssues = isDefaultView
              ? displayIssues
              : sortedIssues.slice(
                  (currentPage - 1) * PAGE_SIZE,
                  currentPage * PAGE_SIZE,
                );

            return (
              <div style={{ flex: 1, overflowY: "auto", padding: "1.25rem" }}>
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "0.75rem",
                  }}
                >
                  <div>
                    <h4
                      style={{
                        fontWeight: 800,
                        color: C.textDk,
                        margin: 0,
                        fontSize: "1rem",
                      }}
                    >
                      Queries
                    </h4>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: C.textGr,
                        marginTop: 2,
                      }}
                    >
                      {issuesLoading
                        ? "Loading…"
                        : isDefaultView
                          ? `Latest ${displayIssues.length} queries`
                          : `${sortedIssues.length} quer${sortedIssues.length !== 1 ? "ies" : "y"} found`}
                    </div>
                  </div>
                  <Btn
                    icon={faPlus}
                    label="Add Query"
                    onClick={() => setIssueDrawer(true)}
                    style={{ backgroundColor: C.mid, color: "#fff" }}
                  />
                </div>

                {/* Compact search bar */}
                <IssueSearchForm
                  allUsers={allUsers}
                  allCrops={allCropsFlat}
                  onSearch={onSearch}
                  onReset={onSearchReset}
                />

                {/* List */}
                {issuesLoading ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: C.textGr,
                      fontSize: "0.85rem",
                    }}
                  >
                    Loading queries…
                  </div>
                ) : pagedIssues.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "3rem",
                      color: C.textGr,
                      backgroundColor: C.white,
                      borderRadius: 12,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      style={{
                        fontSize: "2rem",
                        color: C.border,
                        marginBottom: "0.75rem",
                        display: "block",
                      }}
                    />
                    <div style={{ fontWeight: 600 }}>No queries found</div>
                    <div style={{ fontSize: "0.8rem", marginTop: 4 }}>
                      {isDefaultView
                        ? "No queries exist yet"
                        : "Try adjusting your filters"}
                    </div>
                  </div>
                ) : (
                  <>
                    {pagedIssues.map((iss, i) => (
                      <IssueRow key={iss._id || i} issue={iss} />
                    ))}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 5,
                          marginTop: "1rem",
                        }}
                      >
                        <button
                          onClick={() =>
                            setCurrentPage((p) => Math.max(1, p - 1))
                          }
                          disabled={currentPage === 1}
                          style={{
                            height: 30,
                            padding: "0 12px",
                            borderRadius: 7,
                            border: `1.5px solid ${C.border}`,
                            cursor:
                              currentPage === 1 ? "not-allowed" : "pointer",
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            color: C.textGr,
                            backgroundColor: C.white,
                            opacity: currentPage === 1 ? 0.5 : 1,
                          }}
                        >
                          Prev
                        </button>

                        {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                          .filter(
                            (p) =>
                              p === 1 ||
                              p === totalPages ||
                              Math.abs(p - currentPage) <= 1,
                          )
                          .reduce((acc, p, i, arr) => {
                            if (i > 0 && p - arr[i - 1] > 1) acc.push("…");
                            acc.push(p);
                            return acc;
                          }, [])
                          .map((item, i) =>
                            item === "…" ? (
                              <span
                                key={`ellipsis-${i}`}
                                style={{
                                  fontSize: "0.78rem",
                                  color: C.textGr,
                                  padding: "0 2px",
                                }}
                              >
                                …
                              </span>
                            ) : (
                              <button
                                key={item}
                                onClick={() => setCurrentPage(item)}
                                style={{
                                  height: 30,
                                  width: 30,
                                  borderRadius: 7,
                                  border: `1.5px solid ${item === currentPage ? C.mid : C.border}`,
                                  cursor: "pointer",
                                  fontSize: "0.78rem",
                                  fontWeight: item === currentPage ? 700 : 400,
                                  color:
                                    item === currentPage ? "#fff" : C.textDk,
                                  backgroundColor:
                                    item === currentPage ? C.mid : C.white,
                                }}
                              >
                                {item}
                              </button>
                            ),
                          )}

                        <button
                          onClick={() =>
                            setCurrentPage((p) => Math.min(totalPages, p + 1))
                          }
                          disabled={currentPage === totalPages}
                          style={{
                            height: 30,
                            padding: "0 12px",
                            borderRadius: 7,
                            border: `1.5px solid ${C.border}`,
                            cursor:
                              currentPage === totalPages
                                ? "not-allowed"
                                : "pointer",
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            color: C.textGr,
                            backgroundColor: C.white,
                            opacity: currentPage === totalPages ? 0.5 : 1,
                          }}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

        {/* ══ Tab 3: Reference Material ══ */}
        {pageTab === "reference" &&
          (() => {
            // Flatten all problems from all categories of the selected ref crop
            const allRefProblems = refCrop
              ? (refCrop.categories || []).flatMap((cat) =>
                  (cat.problems || []).map((p) => ({
                    ...p,
                    catName: cat.category,
                  })),
                )
              : [];

            // Search only narrows the crop list — once a crop is selected, all
            // of its problems are shown regardless of the search text.
            const searchLc = refSearch.toLowerCase();
            const filteredRefCrops = crops.filter(
              (c) => !refSearch || c.crop?.toLowerCase().includes(searchLc),
            );
            const filteredRefProblems = allRefProblems;

            return (
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "1.25rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.1rem",
                }}
              >
                {/* ── Header row ── */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "0.75rem",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 9,
                        backgroundColor: C.light,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faBookOpen}
                        style={{ color: C.mid, fontSize: "0.9rem" }}
                      />
                    </div>
                    <div>
                      <div
                        style={{
                          fontWeight: 800,
                          fontSize: "1rem",
                          color: C.textDk,
                        }}
                      >
                        Reference Material
                      </div>
                      <div style={{ fontSize: "0.72rem", color: C.textGr }}>
                        Crop problems, causes &amp; recommended solutions
                      </div>
                    </div>
                  </div>
                  {/* Search */}
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <FontAwesomeIcon
                      icon={faSearch}
                      style={{
                        position: "absolute",
                        left: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: C.textGr,
                        fontSize: "0.72rem",
                      }}
                    />
                    <input
                      value={refSearch}
                      onChange={(e) => {
                        setRefSearch(e.target.value);
                        setRefProblem(null);
                      }}
                      placeholder="Search crop…"
                      style={{
                        width: 230,
                        height: 34,
                        borderRadius: 8,
                        border: `1.5px solid ${C.border}`,
                        paddingLeft: 30,
                        fontSize: "0.83rem",
                        outline: "none",
                        color: C.textDk,
                        boxSizing: "border-box",
                        backgroundColor: C.white,
                      }}
                    />
                    {refSearch && (
                      <button
                        onClick={() => {
                          setRefSearch("");
                          setRefProblem(null);
                        }}
                        style={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: C.textGr,
                          padding: 0,
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faTimes}
                          style={{ fontSize: "0.65rem" }}
                        />
                      </button>
                    )}
                  </div>
                </div>

                {/* ── Crop Selector ── */}
                <div
                  style={{
                    backgroundColor: C.white,
                    borderRadius: 12,
                    border: `1px solid ${C.border}`,
                    padding: "1rem 1.25rem",
                    boxShadow: "0 2px 8px rgba(20,50,30,0.04)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: C.mid,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      marginBottom: "0.75rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faSeedling}
                      style={{ fontSize: "0.65rem" }}
                    />
                    Select Crop
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.65rem",
                      overflowX: "auto",
                      paddingBottom: "0.35rem",
                    }}
                  >
                    {loading ? (
                      <div
                        style={{
                          color: C.textGr,
                          fontSize: "0.85rem",
                          padding: "1rem",
                        }}
                      >
                        Loading crops…
                      </div>
                    ) : filteredRefCrops.length === 0 ? (
                      <div
                        style={{
                          color: C.textGr,
                          fontSize: "0.85rem",
                          padding: "0.5rem",
                        }}
                      >
                        No crops match your search
                      </div>
                    ) : (
                      filteredRefCrops.map((crop) => {
                        const isActive = refCrop?._id === crop._id;
                        return (
                          <button
                            key={crop._id}
                            onClick={() => {
                              setRefCrop(crop);
                              setRefProblem(null);
                            }}
                            style={{
                              flexShrink: 0,
                              width: 172,
                              minHeight: 176,
                              borderRadius: 28,
                              border: `2px solid ${isActive ? C.mid : C.border}`,
                              backgroundColor: isActive ? C.mid : C.white,
                              color: isActive ? "#fff" : C.textDk,
                              cursor: "pointer",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 11,
                              padding: "1.2rem 0.85rem",
                              boxShadow: isActive
                                ? `0 4px 14px rgba(45,90,61,0.28)`
                                : "0 1px 4px rgba(0,0,0,0.06)",
                              transition: "all 0.15s",
                            }}
                          >
                            {getCropImage(crop.crop) ? (
                              <img
                                src={getCropImage(crop.crop)}
                                alt={crop.crop}
                                style={{
                                  width: 90,
                                  height: 90,
                                  objectFit: "contain",
                                  borderRadius: 11,
                                  filter: isActive
                                    ? "brightness(1.15)"
                                    : "none",
                                }}
                              />
                            ) : (
                              <span
                                style={{ fontSize: "3.5rem", lineHeight: 1 }}
                              >
                                {getCropEmoji(crop.crop)}
                              </span>
                            )}
                            <span
                              style={{
                                fontSize: "1.23rem",
                                fontWeight: 700,
                                textAlign: "center",
                                lineHeight: 1.2,
                              }}
                            >
                              {crop.crop}
                            </span>
                            {(crop.categories?.length || 0) > 0 && (
                              <span
                                style={{
                                  fontSize: "1rem",
                                  fontWeight: 600,
                                  padding: "3px 11px",
                                  borderRadius: 11,
                                  backgroundColor: isActive
                                    ? "rgba(255,255,255,0.2)"
                                    : C.light,
                                  color: isActive ? "#fff" : C.mid,
                                }}
                              >
                                {crop.categories.reduce(
                                  (s, c) => s + (c.problems?.length || 0),
                                  0,
                                )}{" "}
                                problems
                              </span>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* ── Problems grid (shown when crop selected) ── */}
                {refCrop && (
                  <div
                    style={{
                      backgroundColor: C.white,
                      borderRadius: 12,
                      border: `1px solid ${C.border}`,
                      padding: "1rem 1.25rem",
                      boxShadow: "0 2px 8px rgba(20,50,30,0.04)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          color: C.mid,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faThLarge}
                          style={{ fontSize: "0.65rem" }}
                        />
                        {refCrop.crop} — Problems
                        <span
                          style={{
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            padding: "1px 8px",
                            borderRadius: 8,
                            backgroundColor: C.light,
                            color: C.mid,
                            textTransform: "none",
                            letterSpacing: 0,
                          }}
                        >
                          {filteredRefProblems.length}
                        </span>
                      </div>
                      {refProblem && (
                        <button
                          onClick={() => setRefProblem(null)}
                          style={{
                            background: "none",
                            border: `1px solid ${C.border}`,
                            borderRadius: 6,
                            padding: "2px 10px",
                            fontSize: "0.72rem",
                            color: C.textGr,
                            cursor: "pointer",
                          }}
                        >
                          Clear selection
                        </button>
                      )}
                    </div>

                    {filteredRefProblems.length === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "2rem",
                          color: C.textGr,
                          fontSize: "0.85rem",
                          border: `1.5px dashed ${C.border}`,
                          borderRadius: 10,
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faBug}
                          style={{
                            fontSize: "1.5rem",
                            color: C.border,
                            marginBottom: "0.5rem",
                            display: "block",
                          }}
                        />
                        No problems found for this crop
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(130px, 1fr))",
                          gap: "0.65rem",
                        }}
                      >
                        {filteredRefProblems.map((p, i) => {
                          const isActive = refProblem?.id === p.id;
                          const openDetail = async () => {
                            const prob = {
                              problemText: p.problemText,
                              catName: p.catName,
                              id: p.id,
                            };
                            setRefProblem(prob);
                            setRefDetailData(null);
                            setRefDrawerOpen(true);
                            if (p.id) {
                              setRefDetailLoading(true);
                              const res = await getProblemDetail(p.id);
                              setRefDetailLoading(false);
                              if (res?.status === 200)
                                setRefDetailData(res.data);
                            }
                          };
                          return (
                            <button
                              key={i}
                              onClick={openDetail}
                              style={{
                                borderRadius: 12,
                                border: `2px solid ${isActive ? C.mid : C.border}`,
                                backgroundColor: isActive ? C.mid : "#f8faf9",
                                color: isActive ? "#fff" : C.textDk,
                                cursor: "pointer",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 6,
                                padding: "0.85rem 0.5rem",
                                minHeight: 100,
                                boxShadow: isActive
                                  ? "0 4px 14px rgba(45,90,61,0.25)"
                                  : "0 1px 3px rgba(0,0,0,0.05)",
                                transition: "all 0.15s",
                              }}
                            >
                              <span
                                style={{ fontSize: "1.6rem", lineHeight: 1 }}
                              >
                                {getCatEmoji(p.catName)}
                              </span>
                              <div>
                                <div
                                  style={{
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    textAlign: "center",
                                    lineHeight: 1.25,
                                  }}
                                >
                                  {p.problemText}
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.62rem",
                                    textAlign: "center",
                                    marginTop: 3,
                                    color: isActive
                                      ? "rgba(255,255,255,0.75)"
                                      : C.textGr,
                                  }}
                                >
                                  {p.catName}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Empty state when no crop selected */}
                {!refCrop && !loading && (
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: C.white,
                      borderRadius: 12,
                      border: `1.5px dashed ${C.border}`,
                      padding: "3rem",
                      textAlign: "center",
                      color: C.textGr,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faBookOpen}
                      style={{
                        fontSize: "2.5rem",
                        color: C.border,
                        marginBottom: "1rem",
                      }}
                    />
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: C.textDk,
                        marginBottom: 6,
                      }}
                    >
                      Select a crop above to explore problems &amp; solutions
                    </div>
                    <div
                      style={{
                        fontSize: "0.82rem",
                        color: C.textGr,
                        maxWidth: 380,
                      }}
                    >
                      Click any crop card to see all known problems, causes,
                      symptoms, and recommended treatments.
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
      </div>

      {/* ── Drawers ────────────────────────────────────────────────────────── */}
      {cropDrawer && (
        <CropFormDrawer
          action={cropDrawer}
          crop={cropDrawer === "edit" ? selected : {}}
          onClose={() => setCropDrawer(null)}
          onSuccess={onCropSuccess}
        />
      )}

      {issueDrawer && (
        <AddIssueDrawer
          onClose={() => setIssueDrawer(false)}
          onSuccess={onIssueSuccess}
        />
      )}

      <RefMaterialDrawer
        open={refDrawerOpen && !!refProblem}
        problem={refProblem}
        cropName={refCrop?.crop}
        initialDetail={refDetailData}
        onClose={() => {
          setRefDrawerOpen(false);
          setRefProblem(null);
          setRefDetailData(null);
        }}
        onSaved={(updated) => setRefDetailData(updated)}
      />
    </div>
  );
}
